import { defineLocalStore } from '@/utils/localStore'
import { computed, reactive, readonly, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { cachedObject } from 'best-modules/utils'
import { confirm, handleAsync, setSnackbar } from 'best-modules/plugins'
import { helpers, required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { useBreadcrumb } from '@/plugins'
import { openDialog } from '@/dialog'
import { notifyValidation } from '@/utils/helpers'
import { initComments } from '@/components/comments/v2'
import { ENTITY_TYPE } from '@/utils/constants'
import {
  incidentDepartmentCreate,
  incidentDepartmentDelete,
  incidentDepartmentList,
  incidentExecutorCreate,
  incidentExecutorDelete,
  incidentExecutorList,
  incidentIssueDelete as incidentIssueDeleteRequest,
  incidentIssueList,
  incidentResolutionCreate,
  incidentRiskAssessmentDelete,
  incidentRiskAssessmentList,
  incidentSetStatus,
  incidentSingle,
  incidentUpdate,
} from '@/requests/incident'
import type {
  Incident,
  IncidentAttachment,
  IncidentCredentials,
  IncidentExecutor,
  IncidentExecutorPayload,
  IncidentIssue,
  IncidentResolutionCredentials,
  IncidentRiskAssessment,
  IncidentStatusPayload,
} from '@/utils/types/incident'
import type { Task } from '@/utils/types/task'
import {
  attachmentCreate,
  attachmentDelete,
  attachmentList,
} from '@/requests/attachment'
import {
  EDITABLE_INCIDENT_STATUS_IDS,
  INCIDENT_STATUS_ACTIONS,
  INCIDENT_STATUS_IDS,
} from '../constants'
import { useCan } from '@/composables/useCan'

export const { initLocalStore: initIncident, useLocalStore: useIncident } =
  defineLocalStore('incidentSingle', () => {
    const route = useRoute()
    const router = useRouter()
    const breadcrumb = useBreadcrumb()
    const { can } = useCan()
    const incidentSource = reactive<Incident>({} as Incident)
    const incidentCredentials = cachedObject<IncidentCredentials>({
      name: '',
      description: null,
      typeId: null,
      categoryId: null,
      occurredDate: null,
      detectedDate: null,
      priorityId: null,
    })
    const incidentId = computed(() => Number(route.params.id))
    const issues = ref<IncidentIssue[]>([])
    const executors = ref<IncidentExecutor[]>([])
    const attachments = ref<IncidentAttachment[]>([])
    const statusHistoryRefreshKey = ref(0)

    const tabs = computed(() => [
      { label: 'Огляд', to: { name: 'incident-overview' } },
      {
        label: 'Недоліки',
        to: { name: 'incident-issues' },
        count: issues.value.length,
      },
      {
        label: 'Виконавці',
        to: { name: 'incident-executors' },
        count: executors.value.length,
      },
      { label: 'Вкладення', to: { name: 'incident-attachments' } },
      { label: 'Коментарі', to: { name: 'incident-comments' } },
      { label: 'Історія змін', to: { name: 'incident-history' } },
    ])

    const getIncidentCredentialsSource = (
      obj: Incident
    ): IncidentCredentials => {
      return {
        name: obj.name || '',
        description: obj.description || null,
        typeId: obj.typeId || null,
        categoryId: obj.categoryId || null,
        occurredDate: obj.occurredDate || null,
        detectedDate: obj.detectedDate || null,
        priorityId: obj.priorityId || null,
      }
    }

    const setIncident = (obj: Incident) => {
      const currentRiskAssessments = riskAssessments.value

      Object.assign(incidentSource, obj, {
        riskAssessments: currentRiskAssessments,
      })
      incidentCredentials.$set(getIncidentCredentialsSource(obj))
      breadcrumb.set([
        {
          index: 0,
          title: 'Операційні ризики',
          to: { name: 'incident-list' },
        },
        {
          index: 1,
          title: obj?.number || obj?.name || 'Інцидент',
          disabled: true,
        },
      ])
    }

    const canEditIncident = computed(() => {
      return (
        can('incidents.update') &&
        EDITABLE_INCIDENT_STATUS_IDS.includes(incidentSource.status?.id)
      )
    })

    const canEditAttachments = computed(() => {
      return (
        can('incidents.update') &&
        (canEditIncident.value ||
          incidentSource.status?.id === INCIDENT_STATUS_IDS.IN_PROGRESS)
      )
    })

    const canCreateIncidentExecutor = computed(() => {
      return (
        can('incidents.create') &&
        [
          INCIDENT_STATUS_IDS.PENDING_REVIEW,
          INCIDENT_STATUS_IDS.IN_PROGRESS,
        ].includes(incidentSource.status?.id)
      )
    })

    const canDeleteIncidentExecutor = computed(() => {
      return (
        can('incidents.update') &&
        incidentSource.status?.id === INCIDENT_STATUS_IDS.PENDING_REVIEW
      )
    })

    const canCreateIncidentDepartment = computed(() => {
      return (
        can('incidents.create') &&
        [
          INCIDENT_STATUS_IDS.PENDING_REVIEW,
          INCIDENT_STATUS_IDS.IN_PROGRESS,
        ].includes(incidentSource.status?.id)
      )
    })

    const canDeleteIncidentDepartment = computed(() => {
      return (
        can('incidents.update') &&
        [
          INCIDENT_STATUS_IDS.PENDING_REVIEW,
          INCIDENT_STATUS_IDS.IN_PROGRESS,
        ].includes(incidentSource.status?.id)
      )
    })

    const canCreateIncidentIssue = computed(() => {
      return (
        can('incidents.create') &&
        incidentSource.status?.id === INCIDENT_STATUS_IDS.IN_PROGRESS
      )
    })

    const canUpdateIncidentIssue = computed(() => {
      return (
        can('incidents.update') &&
        incidentSource.status?.id === INCIDENT_STATUS_IDS.IN_PROGRESS
      )
    })

    const canCreateRiskAssessment = computed(() => {
      return (
        can('incidents.create') &&
        Boolean(incidentSource.status?.id) &&
        incidentSource.status.id !== INCIDENT_STATUS_IDS.RESOLVED
      )
    })

    const statusActions = computed(() => {
      return (INCIDENT_STATUS_ACTIONS[incidentSource.status?.id] || []).filter(
        action => {
          return action.statusId === INCIDENT_STATUS_IDS.RESOLVED
            ? can('incidents.create')
            : can('incidents.update')
        }
      )
    })

    const riskAssessments = computed(() => {
      return Array.isArray(incidentSource.riskAssessments)
        ? incidentSource.riskAssessments
        : []
    })

    const riskAssessmentCount = computed(() => riskAssessments.value.length)

    const totalDamageCost = computed(() => {
      return riskAssessments.value.reduce((acc, assessment) => {
        return acc + Number(assessment.damageCost || 0)
      }, 0)
    })

    const totalDamageCostPotential = computed(() => {
      return riskAssessments.value.reduce((acc, assessment) => {
        return acc + Number(assessment.damageCostPotential || 0)
      }, 0)
    })

    const rules = {
      name: { required },
      typeId: { required },
      categoryId: { required },
      priorityId: { required },
      occurredDate: { required },
      detectedDate: {
        required,
        isAfterOccurredDate: helpers.withMessage(
          'Дата виявлення не може бути раніше дати виникнення',
          () => {
            if (
              !incidentCredentials.detectedDate ||
              !incidentCredentials.occurredDate
            ) {
              return true
            }

            return (
              incidentCredentials.detectedDate >=
              incidentCredentials.occurredDate
            )
          }
        ),
      },
    }

    const v$ = useVuelidate(rules, incidentCredentials)

    const getIncidentData = () => {
      return handleAsync('init', async () => {
        setIncident(await incidentSingle(incidentId.value))
        incidentSource.departments = await incidentDepartmentList(
          incidentId.value
        )
        incidentSource.riskAssessments = await incidentRiskAssessmentList(
          incidentId.value
        )
        await Promise.all([
          getIncidentIssues(),
          getIncidentExecutors(),
          getIncidentAttachments(),
        ])
      })
    }

    const updateIncident = async () => {
      if (!canEditIncident.value && !canEditAttachments.value) return

      await v$.value.$validate()
      if (v$.value.$invalid) {
        notifyValidation(v$.value, 'incident')
        return
      }

      return handleAsync('updateIncident', async () => {
        const departments = incidentSource.departments
        const incident = await incidentUpdate(
          incidentId.value,
          incidentCredentials.$object
        )
        setIncident({
          ...incident,
          departments: incident.departments || departments,
        })
        setSnackbar({
          color: 'success',
          text: 'Інцидент збережено',
        })
      })
    }

    const getIncidentAttachments = () => {
      return attachmentList('incident', incidentId.value).then(res => {
        attachments.value = res
      })
    }

    const createIncidentAttachment = (file: IncidentAttachment) => {
      return attachmentCreate('incident', incidentId.value, file)
    }

    const deleteIncidentAttachment = (file: IncidentAttachment) => {
      return attachmentDelete('incident', incidentId.value, Number(file.id))
    }

    const createIncidentDepartment = (departmentId: number) => {
      if (!canCreateIncidentDepartment.value) return
      if (!departmentId) return

      return handleAsync(
        `createIncidentDepartment_${departmentId}`,
        async () => {
          const department = await incidentDepartmentCreate({
            incidentId: incidentId.value,
            internalDepartmentId: departmentId,
          })
          incidentSource.departments = [
            ...(incidentSource.departments || []),
            department,
          ]
          setSnackbar({
            color: 'success',
            text: 'Підрозділ додано',
          })
        }
      )
    }

    const deleteIncidentDepartment = (departmentId: number) => {
      if (!canDeleteIncidentDepartment.value) return

      return handleAsync(
        `deleteIncidentDepartment_${departmentId}`,
        async () => {
          await incidentDepartmentDelete(departmentId)
          incidentSource.departments = incidentSource.departments.filter(
            item => item.id !== departmentId
          )
          setSnackbar({
            color: 'success',
            text: 'Підрозділ видалено',
          })
        }
      )
    }

    const getIncidentIssues = () => {
      return handleAsync('incidentIssueList', async () => {
        issues.value = await incidentIssueList(incidentId.value)
      })
    }

    const getIncidentExecutors = () => {
      return handleAsync('incidentExecutorList', async () => {
        executors.value = await incidentExecutorList(incidentId.value)
      })
    }

    const addIncidentExecutor = (executor: IncidentExecutor) => {
      executors.value = [executor, ...executors.value]
    }

    const createIncidentExecutor = (credentials: IncidentExecutorPayload) => {
      if (!canCreateIncidentExecutor.value) return

      return handleAsync('incidentExecutorCreate', async () => {
        addIncidentExecutor(await incidentExecutorCreate(credentials))
        setSnackbar({
          color: 'success',
          text: 'Виконавця додано',
        })
      })
    }

    const deleteIncidentExecutor = (executor: IncidentExecutor) => {
      if (!canDeleteIncidentExecutor.value) return

      return confirm({
        text: 'Ви дійсно бажаєте видалити виконавця з інциденту?',
      }).then(() => {
        return handleAsync(
          `incidentExecutorDelete_${executor.id}`,
          async () => {
            await incidentExecutorDelete(executor.id)
            executors.value = executors.value.filter(
              item => item.id !== executor.id
            )
            setSnackbar({
              color: 'success',
              text: 'Виконавця видалено',
            })
          }
        )
      })
    }

    const openIncidentExecutorDialog = () => {
      if (!canCreateIncidentExecutor.value) return

      openDialog({
        name: 'IncidentExecutor',
        dialogData: {
          incidentId: incidentId.value,
        },
        params: {
          cb: createIncidentExecutor,
          validationScope: 'incidentExecutor',
        },
      })
    }

    const addIncidentIssue = (issue: IncidentIssue) => {
      issues.value = [issue, ...issues.value]
    }

    const incidentIssueCreate = () => {
      if (!canCreateIncidentIssue.value) return

      openDialog({
        name: 'IncidentIssue',
        action: 'create',
        dialogData: {
          incidentId: incidentId.value,
        },
        params: {
          cb: addIncidentIssue,
        },
      })
    }

    const incidentIssueUpdate = (issue: IncidentIssue) => {
      if (!canUpdateIncidentIssue.value) return

      openDialog({
        name: 'IncidentIssue',
        action: 'update',
        dialogData: {
          issue: issue,
          incidentId: incidentId.value,
        },
        params: {
          cb: (updatedIssue: IncidentIssue) => {
            issues.value = issues.value.map((item: IncidentIssue) =>
              item.id === updatedIssue.id ? updatedIssue : item
            )
          },
        },
      })
    }

    const incidentIssueDelete = (issueId: number) => {
      if (!canUpdateIncidentIssue.value) return

      return confirm({ text: 'Ви дійсно бажаєте видалити недолік?' }).then(
        () => {
          return incidentIssueDeleteRequest(issueId).then(() => {
            issues.value = issues.value.filter(item => item.id !== issueId)
            setSnackbar({ text: 'Недолік видалено' })
          })
        }
      )
    }

    const incidentIssueSetStatus = (issue: IncidentIssue) => {
      if (!canUpdateIncidentIssue.value) return

      openDialog({
        name: 'IncidentIssueSetStatus',
        dialogData: {
          issue,
        },
        params: {
          cb: (updatedIssue: IncidentIssue) => {
            issues.value = issues.value.map((item: IncidentIssue) =>
              item.id === updatedIssue.id ? updatedIssue : item
            )
          },
        },
      })
    }

    const addIncidentIssueTask = (issueId: number, task: Task) => {
      issues.value = issues.value.map(issue => {
        if (issue.id !== issueId) return issue

        return {
          ...issue,
          tasks: [...(issue.tasks || []), task],
        }
      })
    }

    const openIncidentIssueTaskDialog = (issue: IncidentIssue) => {
      if (!can('tasks.create')) return

      openDialog({
        name: 'IncidentIssueTask',
        dialogData: {
          issue,
        },
        params: {
          cb: task => addIncidentIssueTask(issue.id, task),
        },
      })
    }

    const addRiskAssessment = (riskAssessment: IncidentRiskAssessment) => {
      incidentSource.riskAssessments = [
        ...riskAssessments.value,
        riskAssessment,
      ]
    }

    const deleteRiskAssessment = (riskAssessmentId: number) => {
      if (!canEditIncident.value) return

      return handleAsync(
        `incidentRiskAssessmentDelete_${riskAssessmentId}`,
        async () => {
          await incidentRiskAssessmentDelete(riskAssessmentId)

          incidentSource.riskAssessments = riskAssessments.value.filter(
            item => item.id !== riskAssessmentId
          )
          setSnackbar({
            color: 'success',
            text: 'Оцінку ризику видалено',
          })
        }
      )
    }

    const openRiskAssessmentDialog = () => {
      if (!canCreateRiskAssessment.value) return

      openDialog({
        name: 'IncidentRiskAssessment',
        dialogData: {
          incidentId: incidentId.value,
        },
        params: {
          cb: addRiskAssessment,
        },
      })
    }

    const openResolutionDialog = () => {
      if (!can('incidents.create')) return
      if (incidentSource.resolution) return

      openDialog({
        name: 'IncidentResolution',
        dialogData: {
          incidentId: incidentId.value,
          totalDamageCostPotential: totalDamageCostPotential.value,
        },
        params: {
          cb: createResolution,
        },
      })
    }

    const createResolution = (credentials: IncidentResolutionCredentials) => {
      return handleAsync('incidentResolutionCreate', async () => {
        await incidentResolutionCreate(credentials)
        await getIncidentData()
        statusHistoryRefreshKey.value += 1
        setSnackbar({
          color: 'success',
          text: 'Врегулювання додано',
        })
      })
    }

    const setIncidentStatus = (credentials: IncidentStatusPayload) => {
      return handleAsync('incidentSetStatus', async () => {
        setIncident(await incidentSetStatus(incidentId.value, credentials))
        statusHistoryRefreshKey.value += 1
        setSnackbar({
          color: 'success',
          text: 'Статус інциденту оновлено',
        })
      })
    }

    const openStatusDialog = (
      action: (typeof INCIDENT_STATUS_ACTIONS)[number][number]
    ) => {
      if (
        !statusActions.value.some(item => item.statusId === action.statusId)
      ) {
        return
      }

      if (action.statusId === INCIDENT_STATUS_IDS.RESOLVED) {
        openResolutionDialog()
        return
      }

      openDialog({
        name: 'IncidentSetStatus',
        dialogData: {
          action,
        },
        params: {
          cb: setIncidentStatus,
          submitLabel: action.label,
        },
      })
    }

    const cancelChanges = () => {
      incidentCredentials.$reset()
      v$.value.$reset()
    }

    const backToRegistry = () => {
      router.push({ name: 'incident-list' })
    }

    getIncidentData()
    initComments({
      entityId: incidentId.value,
      entityTypeId: ENTITY_TYPE.INCIDENT,
    })

    return {
      backToRegistry,
      attachments,
      canEditAttachments,
      canCreateIncidentDepartment,
      canCreateIncidentExecutor,
      canCreateIncidentIssue,
      canCreateRiskAssessment,
      canDeleteIncidentDepartment,
      canDeleteIncidentExecutor,
      canEditIncident,
      canUpdateIncidentIssue,
      cancelChanges,
      createIncidentDepartment,
      createIncidentAttachment,
      deleteIncidentDepartment,
      deleteIncidentExecutor,
      deleteIncidentAttachment,
      deleteRiskAssessment,
      executors,
      getIncidentData,
      getIncidentExecutors,
      getIncidentIssues,
      incidentCredentials,
      incidentId,
      incidentSource: readonly(incidentSource),
      issues,
      openRiskAssessmentDialog,
      incidentIssueCreate,
      incidentIssueUpdate,
      incidentIssueDelete,
      incidentIssueSetStatus,
      openIncidentExecutorDialog,
      openIncidentIssueTaskDialog,
      openResolutionDialog,
      riskAssessmentCount,
      riskAssessments,
      statusHistoryRefreshKey,
      statusActions,
      tabs,
      totalDamageCost,
      totalDamageCostPotential,
      openStatusDialog,
      updateIncident,
      v$,
    }
  })
